import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Calendar, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Eye, ClipboardList, User, FileText, AlertCircle, Download,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper for board panel
const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BoardGorevler = () => {
  const { branchId } = useParams();
  const { branches } = useOutletContext();
  const [mainTab, setMainTab] = useState('daily');
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const branch = branches?.find(b => b.id === branchId);
  const branchName = branch?.name || 'Şube';

  useEffect(() => {
    loadTasks();
  }, [branchId, mainTab]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/board/branch/${branchId}/gorevler`, {
        params: { task_type: mainTab },
        headers: getAuthHeaders()
      });
      setTasks(res.data || []);
    } catch (error) {
      console.error('Görevler yüklenirken hata:', error);
      toast.error('Görevler yüklenemedi');
      setTasks([]);
    }
    setLoading(false);
  };

  const loadProgressLogs = async (taskId) => {
    try {
      const res = await axios.get(`${API_URL}/api/work/gorevler/${taskId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
      setProgressLogs([]);
    }
  };

  const handleOpenProgressModal = (task) => {
    setSelectedTask(task);
    loadProgressLogs(task.id);
    setProgressModalOpen(true);
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Sıralanmış veriler
  const sortedTasks = React.useMemo(() => {
    if (!sortConfig.key) return tasks;
    
    return [...tasks].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'created_by_name':
          aValue = (a.created_by_name || '').toLowerCase();
          bValue = (b.created_by_name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'accepted_by':
          aValue = a.accepted_by?.length || 0;
          bValue = b.accepted_by?.length || 0;
          break;
        case 'due_date':
          aValue = new Date(a.start_date || a.due_date || 0).getTime();
          bValue = new Date(b.start_date || b.due_date || 0).getTime();
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { completed: 4, in_progress: 3, pending: 2, cancelled: 1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'completion_date':
          aValue = a.completion_date ? new Date(a.completion_date).getTime() : 0;
          bValue = b.completion_date ? new Date(b.completion_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tasks, sortConfig]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: { label: 'Bekliyor', class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', class: 'bg-blue-100 text-blue-700', icon: RefreshCw },
      completed: { label: 'Tamamlandı', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İptal', class: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    const v = variants[status] || variants.pending;
    const Icon = v.icon;
    return (
      <Badge className={`${v.class} gap-1`}>
        <Icon className="h-3 w-3" />
        {v.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: { label: 'Düşük', class: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', class: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', class: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', class: 'bg-red-100 text-red-600' }
    };
    const v = variants[priority] || variants.normal;
    return <Badge className={v.class}>{v.label}</Badge>;
  };

  const getSourceBadge = (task) => {
    if (task.source === 'branch_task' || task.created_by_branch) {
      return <Badge className="bg-amber-100 text-amber-700">Şube</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">İş Paneli</Badge>;
  };

  // Belge önizlemesi yapılabilir mi?
  const isPreviewable = (type) => {
    return type?.startsWith('image/') || type === 'application/pdf';
  };

  // Belge indir
  const downloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = doc.data;
    link.download = doc.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderTaskTable = () => {
    if (sortedTasks.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Görev Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride görev yok</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-1">
                    Görev
                    <SortIcon columnKey="title" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_by_name')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturan
                    <SortIcon columnKey="created_by_name" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturulma
                    <SortIcon columnKey="created_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('accepted_by')}
                >
                  <div className="flex items-center gap-1">
                    Kabul Edenler
                    <SortIcon columnKey="accepted_by" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('due_date')}
                >
                  <div className="flex items-center gap-1">
                    Tarih Aralığı
                    <SortIcon columnKey="due_date" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center gap-1">
                    Öncelik
                    <SortIcon columnKey="priority" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('completion_date')}
                >
                  <div className="flex items-center gap-1">
                    Tamamlanma Zamanı
                    <SortIcon columnKey="completion_date" />
                  </div>
                </TableHead>
                <TableHead>İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTasks.map(task => (
                <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{task.title}</p>
                      {task.description && (
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{task.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {task.created_by_name || 'Bilinmiyor'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {task.created_at ? new Date(task.created_at).toLocaleDateString('tr-TR', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell>
                    {task.accepted_by && task.accepted_by.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {task.accepted_by.map((a, idx) => (
                          <Badge key={idx} className="bg-green-100 text-green-700 text-xs">
                            {a.user_name || 'Bilinmiyor'}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(task.start_date || task.due_date) ? (
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {task.start_date ? new Date(task.start_date).toLocaleDateString('tr-TR') : new Date(task.due_date).toLocaleDateString('tr-TR')}
                        </div>
                        {task.end_date && (
                          <div className="text-gray-500">
                            → {new Date(task.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                  <TableCell>{getStatusBadge(task.status)}</TableCell>
                  <TableCell>
                    {task.status === 'completed' && task.completion_date ? (
                      <div className="text-sm text-green-600">
                        {new Date(task.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedTask(task); setDetailModalOpen(true); }}
                        data-testid={`view-task-${task.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenProgressModal(task)}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="board-gorevler">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{branchName} - Görevler</h1>
          <p className="text-gray-500">Şube görevlerini görüntüleyin (İş Paneli görevleri dahil)</p>
        </div>
        <Button onClick={loadTasks} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="daily" data-testid="tab-daily">
              <Calendar className="h-4 w-4 mr-2" />
              GÜNLÜK GÖREV
            </TabsTrigger>
            <TabsTrigger value="monthly" data-testid="tab-monthly">
              <Clock className="h-4 w-4 mr-2" />
              AYLIK GÖREV
            </TabsTrigger>
          </TabsList>

          <TabsContent value="daily" className="mt-4">
            {renderTaskTable()}
          </TabsContent>

          <TabsContent value="monthly" className="mt-4">
            {renderTaskTable()}
          </TabsContent>
        </Tabs>
      )}

      {/* Görev Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              Görev Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {getSourceBadge(selectedTask)}
                  {getStatusBadge(selectedTask.status)}
                  {getPriorityBadge(selectedTask.priority)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedTask.title}</h3>
                {selectedTask.description && (
                  <p className="text-gray-600">{selectedTask.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedTask.created_by_name || 'Bilinmiyor'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedTask.created_at 
                      ? new Date(selectedTask.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                {selectedTask.assigned_to_name && (
                  <div>
                    <Label className="text-gray-500">Atanan Kişi</Label>
                    <p className="font-medium">{selectedTask.assigned_to_name}</p>
                  </div>
                )}
                {(selectedTask.start_date || selectedTask.due_date) && (
                  <div>
                    <Label className="text-gray-500">Tarih</Label>
                    <p className="font-medium">
                      {selectedTask.start_date ? new Date(selectedTask.start_date).toLocaleDateString('tr-TR') : new Date(selectedTask.due_date).toLocaleDateString('tr-TR')}
                      {selectedTask.end_date && ` → ${new Date(selectedTask.end_date).toLocaleDateString('tr-TR')}`}
                    </p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedTask.documents && selectedTask.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedTask.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDocument(doc)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedTask.accepted_by && selectedTask.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name || 'Kullanıcı'} - {a.accepted_at ? new Date(a.accepted_at).toLocaleDateString('tr-TR') : '-'}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanma Bilgisi */}
              {selectedTask.status === 'completed' && (
                <div className="pt-4 border-t">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <Label className="text-green-700 mb-2 block flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Tamamlanma Bilgisi
                    </Label>
                    {selectedTask.completion_date && (
                      <p className="text-sm text-green-600">
                        Tarih: {new Date(selectedTask.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                    )}
                    {selectedTask.completion_notes && (
                      <p className="text-sm text-green-700 mt-2">{selectedTask.completion_notes}</p>
                    )}
                    {selectedTask.completed_by_name && (
                      <p className="text-xs text-green-500 mt-1">Tamamlayan: {selectedTask.completed_by_name}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => downloadDocument(previewDoc)}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedTask?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadDocument(doc)}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardGorevler;
